/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { useUserAuth } from '~/components/Auth';
import { ECHO_WIDGET_CDN_URL, ECHO_WIDGET_ID, ECHO_BACKEND_URL, ECHO_EVENTS } from '../constants';
import { isAdminSite } from '~/utils';
import { TLocalAuthContext } from '~/components/Auth';
import { openSupportModal } from '../utils';
import { getExtendedLogger } from '../../Logger';
import logger from '~/config/app.logger';

const log = getExtendedLogger('Echo', logger);

function useEchoWidget(auth?: TLocalAuthContext) {
  const { tempFeatureConfig = {} } = useUserAuth() || {};

  const navigate = useNavigate();
  const isEchoEnabled = !!tempFeatureConfig?.isEchoEnabled;
  const name = auth?.user.name;
  const email = auth?.user.email;
  const lsName = auth?.company.learningSite || window.location.origin;
  const isAdmin = isAdminSite();
  const source = 'support';
  const [isEchoWidgetLoaded, setIsEchoWidgetLoaded] = useState<boolean>(
    typeof window !== 'undefined' && !!window.EchoWidget
  );

  const [isEchoWidgetOpen, setIsEchoWidgetOpen] = useState<boolean>(false);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'showChatBoxToggle' && event.data?.payload?.echo) {
        setIsEchoWidgetLoaded(!!window.EchoWidget);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const init = useCallback(async () => {
    if (!isEchoEnabled) return;

    const widgetId = ECHO_WIDGET_ID;
    const backendUrl = ECHO_BACKEND_URL;
    if (!widgetId || !backendUrl) return;

    try {
      const { Echo } = await import(/* webpackIgnore: true */ ECHO_WIDGET_CDN_URL);

      Echo.init({
        widgetId,
        backendUrl,
        showToggleButton: false,
        alignment: 'left',
        offsetX: '16px',
        offsetY: '16px',
        user: {
          userName: name || '',
          email: email || '',
          role: isAdmin ? 'admin' : 'learner',
          lsName: lsName,
          source: source,
        },
      });

      window.postMessage(
        { type: 'showChatBoxToggle', payload: { echo: true } },
        window.location.origin
      );
    } catch (err) {
      log(`[Echo] Failed to load or initialize widget: ${err}`);
    }
  }, [isEchoEnabled, name, email, isAdmin, lsName, source]);

  useEffect(() => {
    if (!isEchoEnabled) return;

    const onPanelOpened = () => {
      log('[Echo] panel.opened');
      setIsEchoWidgetOpen(true);
    };
    const onPanelClosed = () => {
      log('[Echo] panel.closed');
      setIsEchoWidgetOpen(false);
    };
    const onSupportRequested = async (e: Event) => {
      e.preventDefault();
      await openSupportModal({ navigate })();
    };
    document.addEventListener(ECHO_EVENTS.PANEL_OPENED, onPanelOpened);
    document.addEventListener(ECHO_EVENTS.PANEL_CLOSED, onPanelClosed);
    document.addEventListener(ECHO_EVENTS.SUPPORT_REQUESTED, onSupportRequested);

    return () => {
      document.removeEventListener(ECHO_EVENTS.PANEL_OPENED, onPanelOpened);
      document.removeEventListener(ECHO_EVENTS.PANEL_CLOSED, onPanelClosed);
      document.removeEventListener(ECHO_EVENTS.SUPPORT_REQUESTED, onSupportRequested);
    };
  }, [navigate, isEchoEnabled]);

  const toggleEchoWidget = () => {
    window?.EchoWidget?.toggle();
    setIsEchoWidgetOpen(!isEchoWidgetOpen);
  };

  return { init, toggleEchoWidget, isEchoWidgetLoaded, isEchoWidgetOpen };
}

export default useEchoWidget;
